import { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { encryptURL } from 'utils/functions';
import { toastWarning } from 'utils/toast';
import { RelatoriosContext } from 'contexts/RelatoriosContext';
import { AnuncioContext } from 'contexts/AnuncioContext';
import { DropsContext } from 'contexts/DropsContext';
import { GridContext } from 'contexts/GridContext';
import { AuthContext } from 'contexts/AuthContext';
import { useModal } from 'components/Modals';
import ResumoModal from 'components/Modals/ResumoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import CopyModal from './Modals/CopyModal';
import VincModal from './Modals/VincModal';
import SyncModal from './Modals/SyncModal';
import ImportModal from './Modals/ImportModal';
import columnOverrides from './columnOverrides';
import getAnunciosFilterFields from './filterFields';

const Anúncios = () => {
  const rotina = 'Anuncio';
  const titulo = 'Anúncios';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const { exportPdf, exportXls } = useContext(GridContext);
  const { getURLRelatorio } = useContext(RelatoriosContext);
  const { anuncio, getLoading, postLoading, getAnuncio, atividadeAnuncio } =
    useContext(AnuncioContext);
  const filterFields = useMemo(() => getAnunciosFilterFields(drops), [drops]);
  const loading = getLoading || postLoading;

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const loadGrid = (props = {}) => {
    const filter = props?.filter || getValues();

    closeModal();

    getAnuncio({ ...props, rotina, filter });
  };

  const toolbarActions = [
    {
      name: 'Adicionar',
      icon: 'add',
      placement: 'primary',
      show: !user?.permissoes?.block_anuncios_gerar,
      action: () => navigate('/app/Anuncios/Gerar'),
    },
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({ rotina, filter: getValues(), order: anuncio?.order }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({ rotina, filter: getValues(), order: anuncio?.order }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={anuncio?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getValues() },
              })
            }
          />
        ),
    },
    {
      name: 'Importar Anúncios',
      icon: 'file_upload',
      action: () => openModal(<ImportModal callback={loadGrid} />),
    },
    {
      name: 'Vincular Anúncios',
      icon: 'link',
      action: () =>
        openModal(<VincModal callback={loadGrid} filter={getValues()} />),
    },
    {
      name: 'Sincronizar Anúncios',
      icon: 'sync',
      action: (itens) =>
        itens?.length > 0
          ? openModal(<SyncModal itens={itens} callback={loadGrid} />)
          : toastWarning('Selecione ao menos um anúncio para sincronizar'),
    },
  ];

  const rowActions = [
    {
      name: 'Ir para Anúncio',
      icon: 'open_in_new',
      action: ({ row }) => window.open(row?.detalhes?.link),
      show: ({ row }) => Boolean(row?.detalhes?.link),
    },
    {
      name: 'Copiar',
      icon: 'file_copy',
      action: ({ row }) =>
        openModal(<CopyModal item={row} callback={loadGrid} />),
      menu: true,
    },
    {
      name: 'Editar',
      icon: 'edit',
      show: ({ row }) => !Boolean(row?.agrupamento),
      action: ({ row }) => {
        if (['BLING', 'MERCOS']?.includes(row?.marketplace)) {
          return navigate(`/app/Anuncios/Gerar/${encryptURL(row)}`);
        }

        return navigate(`/app/Anuncios/Editar/${encryptURL(row)}`);
      },
    },
    {
      name: 'Detalhes',
      icon: 'visibility',
      show: ({ row }) => Boolean(row?.agrupamento),
      action: ({ row }) => {
        if (['MERCADO LIVRE']?.includes(row?.marketplace)) {
          return navigate(`/app/Anuncios/Detalhes/${encryptURL(row)}`);
        }

        return toastWarning('Detalhes do anúncio indisponível');
      },
    },
    {
      name: 'Ativar',
      icon: 'key_on',
      show: ({ row }) =>
        !Boolean(row?.agrupamento) && Boolean(row?.ativo !== 'SIM'),
      action: ({ row }) => {
        const data = { id: row?.id };
        atividadeAnuncio({ data, cb: loadGrid });
      },
      menu: true,
    },
    {
      name: 'Inativar',
      icon: 'key_off',
      show: ({ row }) =>
        !Boolean(row?.agrupamento) && Boolean(row?.ativo === 'SIM'),
      action: ({ row }) => {
        const data = { id: row?.id };
        atividadeAnuncio({ data, cb: loadGrid });
      },
      menu: true,
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} titulo={titulo} colunas={anuncio?.colunas} />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={anuncio}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={loading}
          loadGrid={loadGrid}
          getRowHeight={() => 'auto'}
        />
      </Card>
    </Container>
  );
};

export default Anúncios;
